import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { REGIONS, Region } from "../constants/regions";
import { loadConfig, clearConfigCache } from "../config/configLoader";

type RegionContextType = {
  region: Region;
  countryId: string | null;
  changeRegion: (newRegion: Region, countryId?: string) => void;
  /** Persist countryId only (e.g. after countries load and we resolve from region). Does not change region or reload config. */
  setCountryId: (countryId: string) => void;
};

const RegionContext = createContext<RegionContextType | undefined>(undefined);

type RegionProviderProps = {
  children: ReactNode;
};

/**
 * Gets region from URL parameter (?region=ae or ?region=sa)
 */
const getRegionFromUrl = (): Region | null => {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const regionParam = params.get("region")?.toLowerCase();
  if (regionParam === REGIONS.AE || regionParam === REGIONS.SA) {
    return regionParam as Region;
  }
  return null;
};

const STORAGE_KEY = "region";

type StoredRegion = {
  countryName?: string;
  countryId?: string;
};

/**
 * Gets region and countryId from localStorage
 */
const getRegionFromLocalStorage = (): { region: Region | null; countryId: string | null } => {
  if (typeof window === "undefined") return { region: null, countryId: null };
  try {
    const stored: StoredRegion = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    const region = stored?.countryName;
    const countryId = stored?.countryId ?? null;
    if (region === REGIONS.AE || region === REGIONS.SA) {
      return { region: region as Region, countryId };
    }
    return { region: null, countryId };
  } catch (error) {
    console.error("Error reading from localStorage:", error);
  }
  return { region: null, countryId: null };
};

/**
 * Gets region based on domain
 * .sa domain → KSA, .ae domain → UAE, .com domain → KSA (default)
 */
const getRegionFromDomain = (): Region => {
  if (typeof window === "undefined") return REGIONS.SA;
  const hostname = window.location.hostname.toLowerCase();

  if (hostname.endsWith(".ae")) {
    return REGIONS.AE;
  }

  // Default to SA for .sa and .com domains
  return REGIONS.SA;
};

export const RegionProvider: React.FC<RegionProviderProps> = ({ children }) => {
  // Initialize from URL param or localStorage
  const getInitialRegion = (): Region => {
    const urlRegion = getRegionFromUrl();
    if (urlRegion) return urlRegion;
    const { region: localStorageRegion } = getRegionFromLocalStorage();
    if (localStorageRegion) return localStorageRegion;
    return getRegionFromDomain();
  };

  const getInitialCountryId = (): string | null => {
    const urlRegion = getRegionFromUrl();
    const { region: storedRegion, countryId } = getRegionFromLocalStorage();

    // If we have an explicit `?region=...`, only keep the stored `countryId`
    // when it belongs to the same region; otherwise clear it so UI matches the URL.
    if (urlRegion) {
      if (storedRegion === urlRegion) return countryId;
      // Preserve an already-known countryId even when stored countryName is temporarily not AE/SA.
      if (!storedRegion && countryId) return countryId;
      return null;
    }

    // No URL region: keep countryId if available so axios can set X-Region on first refresh request.
    return countryId;
  };

  const [region, setRegion] = useState<Region>(getInitialRegion());
  const [countryId, setCountryId] = useState<string | null>(getInitialCountryId);

  // Initialize region and countryId on first load
  useEffect(() => {
    const initializeRegion = async () => {
      const urlRegion = getRegionFromUrl();
      if (urlRegion) {
        setRegion(urlRegion);
        try {
          const stored = getRegionFromLocalStorage();
          const shouldKeepCountryId =
            stored.region === urlRegion || (!stored.region && !!stored.countryId);
          const nextCountryId = shouldKeepCountryId ? stored.countryId : undefined;

          // Ensure CountrySelector reflects the region from URL (it prefers stored countryId).
          setCountryId(nextCountryId ?? null);

          localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ countryName: urlRegion, countryId: nextCountryId })
          );
        } catch (error) {
          console.error("Error saving to localStorage:", error);
        }
        await loadConfig(urlRegion);

        // Clean up URL after we consume the query param so users don't keep seeing `?region=...`.
        try {
          if (typeof window !== "undefined") {
            const url = new URL(window.location.href);
            url.searchParams.delete("region");
            window.history.replaceState({}, "", url.toString());
          }
        } catch (error) {
          console.error("Error cleaning up region query param:", error);
        }
        return;
      }

      const { region: localStorageRegion, countryId: storedCountryId } = getRegionFromLocalStorage();
      if (localStorageRegion) {
        setRegion(localStorageRegion);
        if (storedCountryId) setCountryId(storedCountryId);
        await loadConfig(localStorageRegion);
        return;
      }

      const domainRegion = getRegionFromDomain();
      setRegion(domainRegion);
      try {
        const stored = getRegionFromLocalStorage();
        const nextCountryId =
          stored.region === domainRegion
            ? stored.countryId
            : !stored.region && stored.countryId
              ? stored.countryId
              : undefined;
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ countryName: domainRegion, countryId: nextCountryId })
        );
      } catch (error) {
        console.error("Error saving to localStorage:", error);
      }
      await loadConfig(domainRegion);
    };

    initializeRegion();
  }, []);

  // Load config when region changes
  useEffect(() => {
    if (region) {
      loadConfig(region).catch((error) => {
        console.error("Failed to load config for region:", error);
      });
    }
  }, [region]);

  const changeRegion = (newRegion: Region, newCountryId?: string) => {
    setRegion(newRegion);
    if (newCountryId !== undefined) {
      setCountryId(newCountryId);
    }
    try {
      const current = getRegionFromLocalStorage();
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          countryName: newRegion,
          countryId: newCountryId ?? current.countryId ?? undefined,
        })
      );
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
    clearConfigCache();
    loadConfig(newRegion).catch((error) => {
      console.error("Failed to load config for region:", error);
    });
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("region");
      window.history.replaceState({}, "", url.toString());
    }
  };

  const setCountryIdOnly = (newCountryId: string) => {
    setCountryId(newCountryId);
    try {
      const current = getRegionFromLocalStorage();
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          countryName: current.region ?? region,
          countryId: newCountryId,
        })
      );
    } catch (error) {
      console.error("Error saving countryId to localStorage:", error);
    }
  };

  return (
    <RegionContext.Provider value={{ region, countryId, changeRegion, setCountryId: setCountryIdOnly }}>
      {children}
    </RegionContext.Provider>
  );
};

export const useRegion = (): RegionContextType => {
  const context = useContext(RegionContext);
  if (!context) {
    throw new Error("useRegion must be used within a RegionProvider");
  }
  return context;
};
